<template>
  <div>
    <h2>
      Film Details
    </h2>

    <el-row>
      <el-col>
        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          :label-position="'top'"
        >

          <div class="form-group">
            <el-tabs
              class="filmmaker_language-adder"
              v-model="activeLanguageTabName"
              tab-position="bottom"
              :type="(showAlternateLanguage) ? 'border-card' : ''"
            >
              <el-tab-pane name="English">
                <span slot="label">
                  <span v-if="showAlternateLanguage" class="is-required--custom">English</span>
                  <span v-else>
                    <el-button v-if="editable" size="small" @click="showAlternateLanguagePanel">
                      <i class="el-icon-edit-outline"/> Click here to add a title and synopsis in another language
                    </el-button>
                  </span>
                </span>
                <el-form-item
                  prop="title"
                >
                  <span slot="label">
                    Title <small>English</small>
                  </span>
                  <el-input
                    v-model="form.title"
                    @input="updateForm('title')"
                    :disabled="!editable"
                    placeholder="Add an English title here"
                  ></el-input>
                </el-form-item>

                <el-form-item
                  prop="description_long"
                >
                  <span slot="label">
                    Synopsis <small>English</small>
                  </span>
                  <el-input
                    v-model="form.description_long"
                    @input="updateForm('description_long')"
                    :disabled="!editable"
                    placeholder="Add an English synopsis here"
                    type="textarea"
                    :autosize="{
                      minRows: 6,
                      maxRows: 14,
                    }"
                  ></el-input>
                </el-form-item>

              </el-tab-pane>

              <el-tab-pane name="Alternate">
                <span slot="label">
                  <span v-if="showAlternateLanguage">
                    <el-select
                      v-model="form.alt_language"
                      @input="updateForm('alt_language')"
                      size="small"
                      placeholder="Select a language"
                      class="no-border"
                      :disabled="!editable"
                    >
                      <el-option
                        v-for="item in langs"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      ></el-option>
                    </el-select>
                  </span>
                  <span v-else></span>
                </span>
                <el-form-item
                  prop="alt_title"
                >
                  <span slot="label">
                    Title <small>{{ this.form.alt_language | decodeLanguageCode }}</small>
                  </span>
                  <el-input
                    v-model="form.alt_title"
                    @input="updateForm('alt_title')"
                    :disabled="!editable"
                  ></el-input>
                </el-form-item>

                <el-form-item
                  prop="alt_description_long"
                >
                  <span slot="label">
                    Synopsis <small>{{ this.form.alt_language | decodeLanguageCode }}</small>
                  </span>
                  <el-input
                    v-model="form.alt_description_long"
                    @input="updateForm('alt_description_long')"
                    :disabled="!editable"
                    type="textarea"
                    :autosize="{
                      minRows: 6,
                      maxRows: 14,
                    }"
                  ></el-input>
                </el-form-item>
              </el-tab-pane>
            </el-tabs>

            <el-form-item
              prop="language"
              label="Language">
              <el-select
                :value="form.language | unwrapLanguageCode"
                @change="updateSelectorData($event, 'language')"
                :disabled="!editable"
                multiple
                allow-create
                filterable
                :placeholder="(editable) ? 'Choose the language of the film' : 'Not selected'"
              >
                <el-option
                  v-for="item in langs"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                ></el-option>
              </el-select>
            </el-form-item>
            <el-form-item
              prop="year"
              label="Year">
              <el-date-picker
                v-model="form.year"
                @input="updateForm('year')"
                :disabled="!editable"
                type="year"
                placeholder="Pick a year"
                :picker-options="{
                  disabledDate(time) {
                    return time.getTime() > Date.now();
                  },
                }"
              ></el-date-picker>
            </el-form-item>
          </div>

          <div class="form-group">
            <el-form-item
              prop="country"
              label="Country">

              <JureeAutoPrefillPopover
                ref="jureePopover"
                :submission="value"
                @input="updateParent"
              />

              <el-select
                :value="form.country | unwrap"
                @change="updateSelectorData($event, 'country')"
                :disabled="!editable"
                multiple
                allow-create
                filterable
                default-first-option
                :placeholder="(editable) ? 'Choose the country' : 'Not selected'">
                <el-option-group
                  v-for="group in countries"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-option-group>
              </el-select>
            </el-form-item>
          </div>

          <sh-spacer by="6"/>

          <div class="form-group">
            <h3>Categorization</h3>

            <el-form-item
              prop="genres"
              label="Genres">
              <el-select
                :value="form.genres | unwrap"
                @change="updateSelectorData($event, 'genres')"
                multiple
                allow-create
                filterable
                default-first-option
                :placeholder="(editable) ? 'Choose the genres' : 'Not selected'"
                :disabled="!editable"
              >
                <el-option-group
                  v-for="group in genres"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-option-group>
              </el-select>
            </el-form-item>

            <el-form-item
              prop="topics"
              label="Topics">
              <el-select
                :value="form.topics | unwrap"
                @change="updateSelectorData($event, 'topics')"
                multiple
                allow-create
                filterable
                default-first-option
                :placeholder="(editable) ? 'Add topics' : 'Not selected'"
                :disabled="!editable"
              >
                <el-option-group
                  v-for="group in topics"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-option-group>
              </el-select>
            </el-form-item>

            <el-checkbox
              @change="updateForm('has_trigger_warning')"
              v-model="form.has_trigger_warning"
            >
              Trigger Warning
            </el-checkbox>
            <el-form-item
              prop="meta_tags"
              label="Tags">
              <p class="tags-note">Flag your content if it contains sensitive material such as nudity & sexual content, suicide & self-harm, vulgar language, violent or graphic content, and political content</p>
              <el-select
                :value="form.meta_tags | unwrap"
                @change="updateSelectorData($event, 'meta_tags')"
                multiple
                allow-create
                filterable
                default-first-option
                :placeholder="(editable) ? 'Add tags' : 'Not selected'"
                :disabled="!editable"
              >
                <el-option-group
                  v-for="group in tags"
                  :key="group.label"
                  :label="group.label">
                  <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-option-group>
              </el-select>
            </el-form-item>
          </div>

        </el-form>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import Vue from 'vue';
import selectorDataMaker from '@/utils/selectorDataMaker';
import debounce from 'lodash/debounce';
// Element-ui
import DatePicker from 'element-ui/lib/date-picker';
// Data
import genresData from '@/assets/data/viddsee/genres';
import tagsData from '@/assets/data/viddsee/tags';
import topicsData from '@/assets/data/viddsee/topics';
import countriesData from '@/assets/data/countries';
import langCodes from '@/assets/data/ISO693-1.json';
// Mixin
import stepMixin from '../Mixins/stepMixin';
// Other component
import JureeAutoPrefillPopover from '../Shared/JureeAutoPrefillPopover';

Vue.use(DatePicker);

export default {
  components: {
    JureeAutoPrefillPopover,
  },
  mixins: [
    stepMixin,
  ],
  name: 'details',
  props: [
    'value',
  ],
  data() {
    return {
      form: {
        // Basic
        title: this.value.title,
        language: this.value.language,
        description_long: this.value.description_long,
        // Alternate language
        alt_title: this.value.alt_title,
        alt_language: this.value.alt_language,
        alt_description_long: this.value.alt_description_long,
        // Other
        year: this.makeDate(this.value.year),
        country: this.value.country,
        // Video attributes
        genres: this.value.genres,
        topics: this.value.topics,
        meta_tags: this.value.meta_tags,
        has_trigger_warning: this.value.has_trigger_warning,
      },
      // Constants
      genres: [{
        label: 'Type to add new genre',
        options: selectorDataMaker.make(genresData),
      }],
      topics: [{
        label: 'Type to add new topic',
        options: selectorDataMaker.make(topicsData),
      }],
      tags: [{
        label: 'Type to add new tag',
        options: selectorDataMaker.make(tagsData),
      }],
      countries: [{
        label: 'Type to add new country',
        options: selectorDataMaker.make(countriesData),
      }],
      langs: Object.freeze(Object.keys(langCodes).map(key => ({
        value: key,
        label: langCodes[key].name,
      }))),
      activeLanguageTabName: 'English',
      wantsAlternateLanguage: false,
    };
  },
  computed: {
    showAlternateLanguage() {
      return this.wantsAlternateLanguage || this.form.alt_title || this.form.alt_synopsis;
    },
  },
  methods: {
    updateSelectorData(value, prop) {
      this.form[prop] = value.join(',');
      this.updateForm(prop);
    },
    updateParent(payload) {
      this.$emit('input', payload);
    },

    updateForm: debounce(function updateForm(prop) {
      this.updateParent({
        ...this.form,
        year: this.parseDate(this.form.year),
      });
    }, 700),
    showAlternateLanguagePanel() {
      this.wantsAlternateLanguage = true;
      setTimeout(() => { this.activeLanguageTabName = 'Alternate'; }, 0);
    },
    // TODO: The date picker is not very flexible
    //       Look look formateDate prop in element-ui future update
    makeDate(year) {
      return (year) ? new Date(year, 0) : new Date();
    },
    parseDate(date) {
      return date.getYear() + 1900;
    },
  },
  filters: {
    unwrap(value) {
      return value ? value.split(',') : [];
    },
    unwrapLanguageCode(value) {
      // TODO: Some of the language label contains ','
      return (value ? value.split(',') : [])
        // This is performed for backward compabitility. Old language code will be transform automatically
        .map(langKey =>
          (langCodes[langKey] && langCodes[langKey].name) || // Attempt to match ISO key
          langKey // Return original if failed
        );
    },
    decodeLanguageCode(value) {
      if (!value) return 'Choose a language below';
      return (langCodes[value] && langCodes[value].name) || value;
    },
  },
  mounted() {
    // Legacy submission information removal
    // Remove directors and cast field when user are migrating from the upgrade
    if (this.value.directors || this.value.cast) {
      this.updateParent({
        directors: '',
        cast: '',
      });
    }
  },
};
</script>

<style lang="scss" scoped>
// TODO: Check element-ui for updates
// Set el-select in this component to block element that take on full width
// This is done to prevent the shaking effect of the selector when there are more than 2 lines of elements
// Take note of https://github.com/ElemeFE/element/issues/2461, https://github.com/ElemeFE/element/issues/8731
.el-select {
  display: block;
}

.el-form-item /deep/ label small {
  font-size: 0.7em;
}

.tags-note {
  margin-top: 0;
  font-size: 11px;
  font-style: italic;
  line-height: 16px;
}

.filmmaker_language-adder {
  margin-bottom: 2em;

  /deep/ {
    .el-tabs__active-bar {
      display: none;
    }
    .el-tabs__item {
      height: initial;
    }
    .el-tabs__nav-wrap::after {
      display: none;
    }
  }
}

.no-border /deep/ input,
.no-border /deep/ .el-input.is-disabled,
.no-border /deep/ .el-input.is-disabled input {
  border: 0;
  background: none;
}
</style>



// WEBPACK FOOTER //
// src/components/Submission/steps/FilmDetail.vue